<template>
  <page-section :title="$t('Integrations_SportsFeedStream_Mappings')">
    <el-button
      slot="controls"
      type="primary"
      icon="el-icon-circle-plus"
      @click="addRow"
      data-qa="sports-feed-stream-create-mapping"
    >
      {{ $t('Integrations_SportsFeedStream_Create_Mapping') }}
    </el-button>

    <rest-list-editable
      ref="rest-list"
      :payload="fetchEndpoint"
      :add-endpoint="saveEndpoint"
      :filterable="false"
      :rules="rules"
      :pagination="false"
      :empty-text="$t('Integrations_SportsFeedStream_Mappings_Empty')"
    >
      <rest-list-column-enum
        prop="event_type"
        :title="$t('Integrations_SportsFeedStream_EventType')"
        :min-width="180"
        :editable="true"
        :sortable="false"
        :enum="sportEvents"
      />

      <rest-list-column
        prop="campaign_id"
        :title="$t('Integrations_SportsFeedStream_Campaign')"
        :min-width="180"
        :editable="true"
        :sortable="false"
      >
        <template slot-scope="{ row }">
          {{ row.campaign_title || row.campaign_id }}
        </template>
        <template slot="input-edit" slot-scope="slot">
          <el-select
            v-model="slot.model.campaign_id"
            value-key="campaign_id"
            filterable
            remote
            :remote-method="autocompleteSearch"
            :loading="searching"
            @focus="autocompleteSearch()"
          >
            <el-option
              v-for="item in options"
              :key="item.id"
              :label="item.title"
              :value="item.id"
            />
          </el-select>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="conditions"
        :title="$t('Integrations_SportsFeedStream_Audience')"
        :min-width="200"
        :sortable="false"
      >
        <template slot-scope="{ row }">
          <div
            v-if="row.conditions"
            data-qa="sports-feed-stream-audience-summary"
          >
            {{ audienceSummary(row.conditions) }}
          </div>
          <div
            v-else
            class="text-warning"
            data-qa="sports-feed-stream-audience-required"
          >
            {{ $t('Integrations_SportsFeedStream_Audience_Required') }}
          </div>
          <el-button
            v-if="row.event_type && row.campaign_id"
            type="text"
            data-qa="sports-feed-stream-edit-audience"
            @click.stop="openAudience(row)"
          >
            {{ $t('Integrations_SportsFeedStream_Audience_Edit') }}
          </el-button>
        </template>
      </rest-list-column>

      <rest-list-column-actions :editable="true">
        <template slot-scope="scope">
          <rest-list-action-delete :endpoint="deleteEndpoint(scope.row)" />
        </template>
      </rest-list-column-actions>
    </rest-list-editable>

    <el-dialog
      :title="$t('Integrations_SportsFeedStream_Audience')"
      :visible.sync="audienceDialogVisible"
      width="70%"
      append-to-body
      data-qa="sports-feed-stream-audience-dialog"
    >
      <conditions-editor
        v-if="audienceDialogVisible"
        :value="audienceConditions"
        @input="audienceConditions = $event"
        :menu="audienceMenu"
        data-model-name="Segment"
        data-model-attribute="conditions"
        attribute-list-data-url="/segment/segment/attribute-list-data"
        :single-level="true"
      />
      <el-alert
        v-if="audienceError"
        type="error"
        :title="audienceError"
        :closable="false"
        class="m-top-20"
      />
      <span slot="footer">
        <el-button @click="clearAudience" :disabled="audienceSaving">
          {{ $t('Integrations_SportsFeedStream_Audience_Clear') }}
        </el-button>
        <el-button @click="audienceDialogVisible = false">
          {{ $t('Cancel') }}
        </el-button>
        <el-button
          type="primary"
          :loading="audienceSaving"
          @click="saveAudience(audienceConditions)"
          data-qa="sports-feed-stream-save-audience"
        >
          {{ $t('Save') }}
        </el-button>
      </span>
    </el-dialog>
  </page-section>
</template>

<script>
import PageSection from '@/common/components/page/PageSection'
import ConditionsEditor from '@/common/components/ConditionsEditor'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListEditable from '@/common/components/rest/RestListEditable'
import { projectEndpoint } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import memoize from 'memoizee'

const emptyAudience = () => ({
  operator: 'AND',
  0: {
    operator: 'AND',
    0: {}
  }
})

const fetchCampaigns = memoize(
  queryString =>
    RestApi(
      projectEndpoint({
        method: 'get',
        url: `campaigns`,
        params: {
          title: queryString,
          status_type: 'live_past_campaigns',
          trigger_type: 'api'
        }
      })
    ).then(({ data }) => data),
  { promise: true, maxAge: 1000 }
)

export default {
  name: 'SportsFeedStreamOverview',

  components: {
    ConditionsEditor,
    RestListEditable,
    RestListActionDelete,
    RestListColumnActions,
    PageSection,
    RestListColumn,
    RestListColumnEnum
  },

  props: {
    model: {
      required: true
    }
  },

  data() {
    return {
      options: [],
      searching: false,
      audienceDialogVisible: false,
      audienceRow: null,
      audienceConditions: emptyAudience(),
      audienceSaving: false,
      eventTypes: {},
      audienceError: ''
    }
  },

  computed: {
    rules() {
      return {
        event_type: {
          required: true,
          trigger: 'blur'
        },
        campaign_id: {
          required: true,
          trigger: 'blur'
        }
      }
    },

    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `integrations/sports-feed-stream/${this.$route.query.id}/mapping`
      })
    },

    saveEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `integrations/sports-feed-stream/${this.$route.query.id}/mapping`
      })
    },

    audienceMenu() {
      return {
        'main-choices': {
          events: {
            title: this.$t('Integrations_SportsFeedStream_Audience_Event'),
            icon: 'list-alt',
            has_submenu: false
          }
        }
      }
    },

    sportEvents() {
      return this.eventTypes
    }
  },

  created() {
    RestApi(
      projectEndpoint({
        method: 'get',
        url: `integrations/sports-feed-stream/${this.$route.query.id}/event-types`
      })
    ).then(({ data }) => {
      this.eventTypes = data || {}
    })
  },

  methods: {
    addRow() {
      this.$refs['rest-list'].addRow({ campaign_id: null, event_type: null })
    },

    deleteEndpoint(row) {
      let eventType = encodeURIComponent(row.event_type)
      let campaignId = encodeURIComponent(row.campaign_id)

      return projectEndpoint({
        method: 'delete',
        url: `integrations/sports-feed-stream/${this.$route.query.id}/mapping/${eventType}/${campaignId}`
      })
    },

    openAudience(row) {
      this.audienceRow = row
      this.audienceConditions = row.conditions
        ? JSON.parse(JSON.stringify(row.conditions))
        : emptyAudience()
      this.audienceError = ''
      this.audienceDialogVisible = true
    },

    clearAudience() {
      this.saveAudience(null)
    },

    saveAudience(conditions) {
      this.audienceSaving = true
      this.audienceError = ''

      RestApi({
        ...this.saveEndpoint,
        data: {
          event_type: this.audienceRow.event_type,
          campaign_id: this.audienceRow.campaign_id,
          conditions: conditions || undefined
        }
      })
        .then(() => {
          this.audienceDialogVisible = false
          this.$refs['rest-list'].$refs.restList.refresh()
        })
        .catch(error => {
          this.audienceError =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            this.$t('Integrations_SportsFeedStream_Audience_Save_Error')
        })
        .finally(() => {
          this.audienceSaving = false
        })
    },

    audienceSummary(conditions) {
      const events = []
      Object.keys(conditions)
        .filter(key => key !== 'operator')
        .forEach(groupKey =>
          Object.keys(conditions[groupKey])
            .filter(key => key !== 'operator')
            .forEach(key => {
              // Saved conditions come back as objects keyed "0", "1"... rather than arrays
              const condition = conditions[groupKey][key]
              if (condition && condition[2]) {
                events.push(condition[2])
              }
            })
        )
      return events.join(', ')
    },

    autocompleteSearch(queryString) {
      this.searching = true
      fetchCampaigns(queryString).then(data => {
        this.options = data
        this.searching = false
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/integrations/types/sportsfeedstream/SportsFeedStreamOverview.vue